import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer, Save } from "lucide-react";
import { Bar, BarChart, Cell, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { statusApi } from "../api/status";
import { useAsync } from "../hooks";
import { useAuth } from "../hooks/useAuth";
import { formatDateTime } from "../utils";
import { changelogChanges } from "../components/Status/statusFormat";
import { ErrorBanner, LoadingText } from "../components/ui";
import { SeverityBadge } from "../components/Status/badges";
import type { HealthEvent } from "../types/health";

const ACTION_LABEL: Record<string, string> = {
  "incident.opened": "Incident opened",
  "incident.resolved": "Marked resolved",
  "incident.auto_resolved": "Auto-resolved",
  "incident.overridden": "Overridden",
  "incident.signals_attached": "Signals attached",
  "incident.severity_escalated": "Severity escalated",
  "incident.deleted": "Deleted",
  "incident.postmortem_updated": "Post-mortem updated",
};

function esc(value: unknown): string {
  return String(value ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] ?? c));
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return "ongoing";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function platformLabel(platform: string): string {
  return platform === "shared" ? "All" : platform.charAt(0).toUpperCase() + platform.slice(1);
}

function incidentNumber(id: string): string {
  return `INC-${id.slice(0, 8).toUpperCase()}`;
}

function StatCard({ label, children, footer }: { label: string; children: React.ReactNode; footer?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-5 py-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{label}</p>
      <div className="mt-2 text-2xl font-bold tabular-nums text-gray-900">{children}</div>
      {footer && <p className="mt-1 text-xs text-gray-400">{footer}</p>}
    </div>
  );
}

export default function IncidentPostmortemPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ["admin", "super_admin"].includes(user?.role?.name ?? "");

  const { data, loading, error, reload } = useAsync(
    () => statusApi.getIncidentPostmortem(id).then((response) => response.data),
    [id],
    { errorMessage: "Could not load this post-mortem" },
  );

  const [notes, setNotes] = useState("");
  const [rootCause, setRootCause] = useState("");
  const [regLost, setRegLost] = useState("");
  const [ggr, setGgr] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!data) return;
    const pm = data.postmortem;
    setNotes(pm?.notes ?? "");
    setRootCause(pm?.root_cause ?? data.suggested_root_cause ?? "");
    setRegLost(pm?.impact?.registrations_lost != null ? String(pm.impact.registrations_lost) : "");
    setGgr(pm?.impact?.ggr_impact != null ? String(pm.impact.ggr_impact) : "");
  }, [data]);

  async function handleSave() {
    setSaving(true);
    try {
      await statusApi.saveIncidentPostmortem(id, {
        notes: notes.trim() || null,
        root_cause: rootCause.trim() || null,
        impact: {
          registrations_lost: regLost.trim() === "" ? null : Number(regLost),
          ggr_impact: ggr.trim() === "" ? null : Number(ggr),
          currency: "GBP",
          note: null,
        },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      reload();
    } catch {
      setSaved(false);
    } finally {
      setSaving(false);
    }
  }

  function exportPdf() {
    if (!data) return;
    const win = window.open("", "_blank", "width=820,height=1000");
    if (!win) return;
    const inc = data.incident;
    const ci = data.computed_impact;
    const chartSvg = document.querySelector("#pm-impact-chart svg")?.outerHTML ?? "";

    const timelineRows = (data.timeline ?? [])
      .map((e) => `<li><span class="t">${esc(formatDateTime(e.occurred_at))} · ${esc(e.source)} · ${esc(e.severity ?? "")}</span><br/><b>${esc(e.component ?? "Signal")}</b>${e.message ? `<br/>${esc(e.message)}` : ""}</li>`)
      .join("");
    const changelogRows = data.changelog
      .map((entry) => {
        const lines = changelogChanges(entry.meta).map((l) => `<br/>${esc(l)}`).join("");
        const reason = (entry.meta as { reasoning?: string } | null)?.reasoning;
        return `<li><b>${esc(ACTION_LABEL[entry.action] ?? entry.action)}</b> — ${esc(formatDateTime(entry.created_at))} · ${esc(entry.actor)}${lines}${reason ? `<br/><i>"${esc(reason)}"</i>` : ""}</li>`;
      })
      .join("");

    const regLostVal = regLost || (ci ? `~${ci.registrations_lost}` : "—");
    const ggrVal = ggr ? `£${ggr}` : "—";
    const impactDetail = ci
      ? `<p class="t" style="margin-top:6px">Correlated from warehouse · ±${ci.confidence_pct}% · ${ci.baseline_weeks}-week baseline · ${ci.registrations_actual} actual vs ${ci.registrations_expected} expected</p>`
      : "";

    win.document.write(`<!doctype html><html><head><title>Post-mortem — ${esc(inc.service_name)}</title>
      <style>body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#111;margin:40px;max-width:720px}
      h1{font-size:20px;margin:0 0 4px} .meta{color:#555;font-size:13px;margin-bottom:16px}
      h2{font-size:14px;margin:22px 0 8px;border-bottom:1px solid #eee;padding-bottom:4px}
      ul{list-style:none;padding:0} li{border-left:2px solid #ddd;padding:0 0 12px 14px;margin-left:6px}
      table{border-collapse:collapse;font-size:13px} td{padding:4px 16px 4px 0}
      td.k{color:#666} .chart{max-width:360px;margin:10px 0} .chart svg{max-width:100%}
      .t{color:#888;font-size:11px;text-transform:uppercase;letter-spacing:.05em}</style></head>
      <body><h1>Post-mortem — ${esc(inc.service_name)}</h1>
      <p class="meta">${esc(incidentNumber(id))} · ${esc(inc.severity)} · ${esc(platformLabel(inc.platform))} · Detected ${esc(formatDateTime(inc.started_at))} · Resolved ${inc.resolved_at ? esc(formatDateTime(inc.resolved_at)) : "ongoing"} · ${esc(formatDuration(inc.duration_seconds))}</p>

      <h2>Business impact</h2>
      <table>
        <tr><td class="k">Registrations lost</td><td>${esc(regLostVal)}</td></tr>
        <tr><td class="k">GGR impact</td><td>${esc(ggrVal)}</td></tr>
        <tr><td class="k">Downtime</td><td>${esc(formatDuration(inc.duration_seconds))}</td></tr>
      </table>
      ${impactDetail}
      ${chartSvg ? `<div class="chart">${chartSvg}</div>` : ""}

      <h2>Root cause</h2><p>${rootCause ? esc(rootCause) : "—"}</p>
      <h2>Notes / follow-up</h2><p>${notes ? esc(notes) : "—"}</p>

      <h2>Signal timeline</h2><ul>${timelineRows || "<li>No signals recorded.</li>"}</ul>

      <h2>Audit trail</h2>
      <table>
        <tr><td class="k">Detected</td><td>${esc(formatDateTime(inc.started_at))}</td></tr>
        <tr><td class="k">Resolved</td><td>${inc.resolved_at ? esc(formatDateTime(inc.resolved_at)) : "—"}</td></tr>
        <tr><td class="k">Retained</td><td>Indefinitely</td></tr>
      </table>

      <h2>Changelog</h2><ul>${changelogRows || "<li>No changes recorded.</li>"}</ul>
      </body></html>`);
    win.document.close();
    win.focus();
    win.print();
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mb-4 flex items-center gap-1.5 text-sm">
        <button onClick={() => navigate("/status?tab=incidents")} className="flex items-center gap-1.5 text-blue-600 hover:text-blue-700">
          <ArrowLeft size={14} aria-hidden="true" />
          Incidents
        </button>
        <button onClick={() => navigate(`/status/incidents/${id}`)} className="text-blue-600 hover:text-blue-700">
          › {incidentNumber(id)}
        </button>
        <span className="text-gray-400">› Post-mortem</span>
      </div>

      {loading && !data ? (
        <LoadingText>Loading post-mortem…</LoadingText>
      ) : error ? (
        <ErrorBanner>{error}</ErrorBanner>
      ) : !data ? null : (
        <>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Post-mortem — {data.incident.service_name}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-500">
                <SeverityBadge severity={data.incident.severity} />
                <span>{platformLabel(data.incident.platform)}</span>
                <span>· {formatDuration(data.incident.duration_seconds)}</span>
              </div>
            </div>
            <button
              onClick={exportPdf}
              className="flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:border-gray-300 hover:bg-gray-50"
            >
              <Printer size={14} aria-hidden="true" />
              Export PDF
            </button>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            <StatCard label="Registrations lost">
              {isAdmin ? (
                <input
                  type="number"
                  value={regLost}
                  onChange={(e) => setRegLost(e.target.value)}
                  placeholder={data.computed_impact ? `~${data.computed_impact.registrations_lost}` : "—"}
                  className="w-full rounded-lg border border-gray-200 px-2 py-1 text-2xl font-bold text-red-600 focus:border-blue-400 focus:outline-none"
                />
              ) : (
                <span className="text-red-600">
                  {regLost || (data.computed_impact ? `~${data.computed_impact.registrations_lost}` : "—")}
                </span>
              )}
            </StatCard>
            <StatCard label="GGR impact (est.)">
              {isAdmin ? (
                <div className="flex items-center gap-1">
                  <span className="text-red-600">£</span>
                  <input
                    type="number"
                    value={ggr}
                    onChange={(e) => setGgr(e.target.value)}
                    placeholder="—"
                    className="w-full rounded-lg border border-gray-200 px-2 py-1 text-2xl font-bold text-red-600 focus:border-blue-400 focus:outline-none"
                  />
                </div>
              ) : (
                <span className="text-red-600">{ggr ? `£${ggr}` : "—"}</span>
              )}
            </StatCard>
            <StatCard label="Downtime" footer="Counts toward monthly uptime">
              {formatDuration(data.incident.duration_seconds)}
            </StatCard>
          </div>

          {data.computed_impact && (
            <div className="mt-5 rounded-xl border border-gray-200 bg-white p-5">
              <h2 className="text-base font-semibold text-gray-900">Registrations during the outage</h2>
              <p className="mt-0.5 text-xs text-gray-400">
                Correlated from warehouse · ±{data.computed_impact.confidence_pct}% · {data.computed_impact.baseline_weeks}-week baseline
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-8">
                <div>
                  <p className="text-3xl font-bold tabular-nums text-red-600">~{data.computed_impact.registrations_lost}</p>
                  <p className="text-xs text-gray-500">registrations lost</p>
                  <p className="mt-1 text-xs text-gray-400">
                    {data.computed_impact.registrations_actual} actual vs {data.computed_impact.registrations_expected} expected
                  </p>
                </div>
                <div id="pm-impact-chart" className="h-[120px] min-w-[180px] flex-1">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        { name: "Expected", value: data.computed_impact.registrations_expected },
                        { name: "Actual", value: data.computed_impact.registrations_actual },
                      ]}
                      margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
                    >
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
                      <YAxis hide domain={[0, "dataMax"]} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                        <Cell fill="#93c5fd" />
                        <Cell fill="#dc2626" />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {data.timeline && data.timeline.length > 0 && (
            <div className="mt-5 rounded-xl border border-gray-200 bg-white p-5">
              <h2 className="mb-3 text-base font-semibold text-gray-900">Signal timeline</h2>
              <ol className="space-y-3">
                {data.timeline.map((event: HealthEvent) => (
                  <li key={event.id} className="flex items-start justify-between gap-4 border-b border-gray-50 pb-3 last:border-0 last:pb-0">
                    <div className="min-w-0">
                      <p className="text-xs font-medium uppercase tracking-wider text-gray-400">
                        {event.source} · {event.category}
                      </p>
                      <p className="mt-0.5 text-sm font-semibold text-gray-900">{event.component ?? "Signal"}</p>
                      {event.message && <p className="mt-0.5 text-sm text-gray-500">{event.message}</p>}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      {event.severity && <SeverityBadge severity={event.severity} />}
                      <span className="whitespace-nowrap text-xs text-gray-400">{formatDateTime(event.occurred_at)}</span>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <div className="mt-5 grid gap-5 lg:grid-cols-[1fr_320px]">
            <section className="rounded-xl border border-gray-200 bg-white p-5">
              <h2 className="mb-3 text-base font-semibold text-gray-900">Notes &amp; root cause</h2>
              {isAdmin ? (
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-500">Root cause</label>
                    <textarea
                      rows={3}
                      value={rootCause}
                      onChange={(e) => setRootCause(e.target.value)}
                      className="w-full resize-y rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-500">Notes / follow-up</label>
                    <textarea
                      rows={4}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="w-full resize-y rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex items-center gap-1.5 rounded-lg bg-[#1D345F] px-4 py-2 text-sm font-medium text-white hover:bg-[#162847] disabled:opacity-50"
                    >
                      <Save size={13} />
                      {saving ? "Saving…" : saved ? "Saved!" : "Save notes"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 text-sm text-gray-700">
                  {rootCause && <p><span className="font-medium">Root cause:</span> {rootCause}</p>}
                  {notes && <p>{notes}</p>}
                  {!rootCause && !notes && <p className="text-gray-400">No notes recorded yet.</p>}
                </div>
              )}
            </section>

            <aside className="space-y-5">
              <div className="rounded-xl border border-gray-200 bg-white p-5">
                <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Audit trail</h2>
                <div className="flex items-center justify-between border-b border-gray-100 py-2.5">
                  <span className="text-sm text-gray-500">Detected</span>
                  <span className="text-sm font-medium text-gray-900">{formatDateTime(data.incident.started_at)}</span>
                </div>
                <div className="flex items-center justify-between border-b border-gray-100 py-2.5">
                  <span className="text-sm text-gray-500">Resolved</span>
                  <span className="text-sm font-medium text-gray-900">
                    {data.incident.resolved_at ? formatDateTime(data.incident.resolved_at) : "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2.5">
                  <span className="text-sm text-gray-500">Retained</span>
                  <span className="text-sm font-medium text-gray-900">Indefinitely</span>
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-5">
                <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Changelog</h2>
                {data.changelog.length === 0 ? (
                  <p className="text-sm text-gray-400">No changes recorded.</p>
                ) : (
                  <ul className="space-y-3">
                    {data.changelog.map((entry, index) => (
                      <li key={index} className="text-sm">
                        <p className="font-medium text-gray-800">{ACTION_LABEL[entry.action] ?? entry.action}</p>
                        <p className="text-xs text-gray-400">
                          {formatDateTime(entry.created_at)} · {entry.actor}
                        </p>
                        {changelogChanges(entry.meta).map((line, j) => (
                          <p key={j} className="mt-0.5 text-xs text-gray-500">{line}</p>
                        ))}
                        {(entry.meta as { reasoning?: string } | null)?.reasoning && (
                          <p className="mt-0.5 text-xs italic text-gray-400">
                            "{(entry.meta as { reasoning?: string }).reasoning}"
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
